import React, {useEffect, useState} from 'react';
import DefaultLayout from "components/DefaultLayout";
import Spinner from 'components/Spinner';
import {connect} from 'react-redux';
import {Button} from "ui";
import {FormattedMessage, useIntl} from "react-intl";
import moment from "moment/moment";
import Badge from "components/CampaignCard/components/Badge";
import {Card, Empty, message, Tag} from "antd";
import StudentProgramFilter from "components/StudentProgramFilter";
import useHandleError from "utils/useHandleError";
import coursesApiClient from "utils/coursesApiClient";
import FileDownload from "js-file-download";
import './styles.scss';
import {config} from "config/config";
import { useLocaleContext } from 'context/LocaleContext';

const mapStateToProps = (state: any) => {
    return {
        session: state.session,
    }
}

const MyCertificates: React.FC<any> = ({session, userTypeId = null}) => {
    const { locale } = useLocaleContext();
    const [isLoading, setIsLoading] = useState(false);
    const intl = useIntl();
    const [filtersSelected, setFiltersSelected] = useState<any>({
        validity: 'valid',
        sorting: 'asc',
    });
    const [searchString, setSearchString] = useState<string>('');
    const [reload, setReload] = useState<boolean>(false);
    const [programsList, setProgramsList] = useState<any>([]);
    const [programsListShow, setProgramsListShow] = useState<any>([]);
    const [handleError] = useHandleError();

    const getProgramTitle = (program: any) =>
        program.title?.[locale] ||
        program.title?.[Object.keys(program.title || {})[0]] ||
        program.certificateId ||
        '';

    useEffect(() => {
        if ((session.active_user_type_id && session.active_user_type === 'STUDENT') || userTypeId) {
            loadPrograms();
        }
    }, []);

    useEffect(() => {
        loadPrograms();
    }, [searchString]);

    const loadPrograms = async () => {
        setIsLoading(true);

        let url = `/api/v1/courses/certificates/student?search=${searchString}`

        if (userTypeId) {
            url += `&userTypeId=${userTypeId}`;
        }

        try {
            const response = await coursesApiClient.request(url, [], 'GET');
            if (!response.programs) {
                return;
            }
            setProgramsList(response.programs);
        } catch (error) {
            handleError(error);
        } finally {
            setIsLoading(false);
        }
    }

    const downloadFile = async (name: string, id: number) => {
        try {
            let response = await coursesApiClient.request(`api/v1/courses/storage-file/${id}`, {}, 'GET', true, true);
            FileDownload(response, name);
        } catch (error) {
            console.error(error);
            message.error(intl.formatMessage({id: 'error.data_load'}));
        }
    }

    useEffect(() => {
        showPrograms();
    }, [filtersSelected, programsList])

    const showPrograms = () => {
        let showCertificates: any = programsList.map((c: any) => ({
            ...c
        }));

        let show: any = [];

        if (filtersSelected.validity !== undefined) {
            if (filtersSelected.validity === 'valid') {
                Object.values(showCertificates).forEach((program: any) => {
                    let today = moment();
                    let expDate = moment(program.expirationDate);
                    if (program.validIndefinite === 1 || expDate.diff(today, 'seconds') > 0) {
                        show.push(program);
                    }
                });
            } else if (filtersSelected.validity === 'expired') {
                Object.values(showCertificates).forEach((program: any) => {
                    let today = moment();
                    let expDate = moment(program.expirationDate);
                    if (program.validIndefinite === 0 && expDate.diff(today, 'seconds') < 0) {
                        show.push(program);
                    }
                });
            } else {
                show = [...showCertificates];
            }
        } else {
            show = [...showCertificates];
        }

        show.sort((a: any, b: any) => {
            const titleA = getProgramTitle(a);
            const titleB = getProgramTitle(b);

            if (filtersSelected.sorting === 'desc') {
                return titleB.localeCompare(titleA);
            }

            return titleA.localeCompare(titleB);
        });

        setProgramsListShow(show);
    };

    const tags = function (validIndefinite: any, issueDate: any, expirationDate: any, termLimitValue: any) {
        if (validIndefinite === 1) {
            return (
                <Tag color={'green'}>
                    <FormattedMessage id={'general.valid'}/>
                </Tag>
            )
        } else {
            let today = moment();
            let expDate = moment(expirationDate);

            if (expDate.diff(today, 'seconds') > 0) {
                if (expDate.diff(today, 'days') > termLimitValue) {
                    return (
                        <Tag color={'green'}>
                            <FormattedMessage id={'general.valid'}/>
                        </Tag>
                    )
                } else {
                    return (
                        <Tag color={'orange'}>
                            <FormattedMessage id={'general.expires_soon'}/>
                        </Tag>
                    )
                }
            } else if (expDate.diff(today, 'seconds') < 0) {
                return (
                    <Tag color={'red'}>
                        <FormattedMessage id={'general.expired'}/>
                    </Tag>
                )
            }
        }
    }

    const renderPrograms = (list: any) => {
        if (!Object.keys(list).length) {
            return (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={intl.formatMessage({id: 'general.found_no_data'})}
                />
            );
        }
        return (
            <div className={'my-certificates'}>
                {
                    Object.values(list).map((program: any) => {
                        return (
                            <Card key={program.id}>
                                <div className="container">
                                    <div className="details">
                                        <div className="badge">
                                            {!!program.title ? (
                                                <Badge
                                                    thumbnail={program.thumbnail}
                                                    results={'100'}
                                                />
                                            ) : (
                                                <Badge
                                                    thumbnail={'fa-file-certificate'}
                                                    results={'100'}
                                                />
                                            )}
                                        </div>
                                        <div className="heading">
                                            {getProgramTitle(program)}
                                        </div>
                                        <div className="date">
                                            <span>
                                                <FormattedMessage id={'general.issue_date'} />:{' '}
                                                {moment(program.issueDate).format(
                                                    config.defaultDateFormat
                                                )}
                                            </span>
                                            <span>
                                                <FormattedMessage id={'general.expiring_date'} />:
                                                {!program.validIndefinite
                                                    ? moment(program.expirationDate).format(
                                                          config.defaultDateFormat
                                                      )
                                                    : ' -'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="tags">
                                        {tags(
                                            program.validIndefinite,
                                            program.issueDate,
                                            program.expirationDate,
                                            program.termLimitValue
                                        )}
                                    </div>
                                    {program.storageId && program.storageId !== 0 ? (
                                        <div className="download">
                                            <Button
                                                type={'default'}
                                                onClick={() =>
                                                    downloadFile(program.name, program.storageId)
                                                }
                                                message='general.download'
                                            />
                                        </div>
                                    ) : (
                                        <div></div>
                                    )}
                                </div>
                            </Card>
                        );
                    })
                }
            </div>
        )
    }

    return (
        <Spinner spinning={isLoading}>
            <DefaultLayout.PageLayout withoutPageLayout>
                <StudentProgramFilter
                    setSearchString={setSearchString}
                    setReload={setReload}
                    filtersSelected={filtersSelected}
                    setFiltersSelected={setFiltersSelected}
                    programsList={programsList}
                    status={false}
                    competences={false}
                    validity={true}
                    sorting={true}
                />
                <div className="program-list-wrapper program-list ">
                    {renderPrograms(programsListShow)}
                </div>
            </DefaultLayout.PageLayout>
        </Spinner>
    )
};

export default connect(mapStateToProps)(MyCertificates);
